import { memo, useCallback, useMemo, useState } from "react";
import { Card, Grid, CardContent, Stack, Typography, Link, CardActions, Button, Box, IconButton, Tooltip, useMediaQuery, CardActionArea, Chip } from '@mui/material';
import { ArrowOutwardRounded, CheckBoxOutlineBlankRounded, CheckBoxRounded, PhoneRounded, ReceiptRounded, SingleBedRounded } from "@mui/icons-material";
import { formatCurrency } from "utils";
import { grey, pink, brown } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import ScrollDialog from 'components/ScrollDialog';
import InmatePaymentCollection from './InmatePaymentCollection';
import { downloadPaymentReceipt } from "services/payments";
import PaymentHistory from "./PaymentHistory";
import InlineCircularProgress from "components/InlineCircularProgress";
import { paymentTypesObj } from "utils/appConstants";
import RentDueReminders from "./RentDueReminders";
import PaymentUpdate from "./PaymentUpdate";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} placement="right-end" enterTouchDelay={0} leaveTouchDelay={3000} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    // fontSize: 12,
  },
}));

// const defaultValues = {collect: false, paymentId: null, paymentData: null};
const PaymentCard = ({data={}, onPaymentSuccess, isMultiplePaymentEnable, togglePaymentSelection, isPaymentSelected, isCollectPaymentsAllowed}) => {
  const { paymentId, inmateName, description, amountDue=0, mobile, rent, status, amountPaid=0, checkInDate, bedInfo, paymentType } = data;
  const [showPaymentCollectionModal, setShowPaymentCollectionModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [showPaymentUpdateModal, setShowPaymentUpdateModal] = useState(false);

  const paymentUpdateData = useMemo(() => ({
    paymentMode: data?.paymentMode || '',
    paymentDate: data?.paymentDate || '',
    bankName: data?.bankName || '',
    refNo: data?.refNo || '',
    comments: data?.comments || '',
  }), [data]);
  const onModalClose = useCallback(() => {
    setShowPaymentCollectionModal(false);
  }, []);

  return (
    <Grid item xs={12} sm={10} md={6} lg={4} xl={4}>
      <Card 
        sx={{
          ':hover': {boxShadow: {md: 'rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px'}}, 
          height: '100%', 
          boxShadow: {xs: 0, md: `rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px`}, 
          borderRadius:{ xs: isPaymentSelected ? 2.2 : 0, md: 2.2 },
          borderBottom: {xs: `1px solid ${grey[200]}`, md: 0},
          backgroundColor: isPaymentSelected ? pink[50] : '#fff',
          position: 'relative' 
        }}
      >
        <CardActionArea 
          disableRipple={!isMultiplePaymentEnable} 
          disableTouchRipple={!isMultiplePaymentEnable} 
          onClick={() => isMultiplePaymentEnable ? togglePaymentSelection(paymentId) : false}
          sx={{cursor: 'inherit', ':hover .MuiCardActionArea-focusHighlight': { opacity: 0}}}
          component="div" 
        >
          <CardContent sx={{p: 1.5, pb: `2px !important`}}>
            {isMultiplePaymentEnable ? isPaymentSelected ? 
              <CheckBoxRounded sx={{position: 'absolute', left: 6, top: 6, fontSize: '1.5rem', color: 'secondary.main', backgroundColor: '#fff', zIndex: 1, borderRadius: 1}} />: 
              <CheckBoxOutlineBlankRounded sx={{position: 'absolute', left: 6, top: 6, fontSize: '1.5rem', color: 'secondary.main', backgroundColor: '#fff', zIndex: 1, borderRadius: 1}} />
            : null}
            <Stack direction="column" gap={1} justifyContent="space-between" sx={{height: '100%'}}>
              <Stack direction="row" gap={1} alignItems="center" px={1}>
                {/* {status === 'PAID' ? 
                  <ReceiptRounded sx={{opacity: .5, fontSize: '2.4rem', p: .4, backgroundColor: 'success.main', color: '#fff', borderRadius: 2}} color="success" /> :
                  <ArrowOutwardRounded sx={{opacity: .6, fontSize: '2.4rem', backgroundColor: 'secondary.main', color: '#fff', borderRadius: 2}}  /> 
                } */}
                <Stack flexGrow={1}>
                  <Typography variant="h6" style={{fontSize: 18}}>{inmateName}</Typography>
                  <Typography variant="body2" color="grey">{description}</Typography>
                </Stack>
                <Stack>
                  <Typography variant="h6" color={status === 'PAID' ? 'success.main' : 'secondary'} >{formatCurrency(status === 'PAID' ? amountPaid : amountDue)}</Typography>
                </Stack>
              </Stack>
              <Stack direction="row" gap={1} justifyContent="space-between" alignContent="center">
                <Stack direction="row" gap={1} alignItems="center">
                  <LightTooltip 
                    title={
                      <>
                      <Typography variant="body2" color="#555">{bedInfo}</Typography>
                      <Typography variant="body2" color="#555">{checkInDate}</Typography>
                      </>
                    }
                  >
                    <IconButton size="small" aria-label="Bed details">
                      <SingleBedRounded color="tertiary" style={{opacity: .5}} />
                    </IconButton>
                  </LightTooltip>
                  <Stack direction="row" gap={.25} alignItems="center">
                    <PhoneRounded fontSize="1.2rem" sx={{color: grey[500]}} />
                    <Typography variant="caption" color="grey"><Link href={`tel:${mobile}`} underline="none" color="tertiary.main">{mobile}</Link></Typography>
                  </Stack>
                </Stack>
                <Typography variant="caption" color="grey">Rent: <span style={{color: '#555'}}>{formatCurrency(rent)}</span></Typography>
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
        {isMultiplePaymentEnable ? null : (
          <CardActions>
            <Stack direction="row" gap={1} justifyContent="space-between" width="100%">
              <Stack direction="row" alignItems="center">
                {status === 'PAID' ? null : <RentDueReminders paymentId={paymentId} isInmatePaymentReminder={true} />}
                <Chip sx={{backgroundColor: brown[50], color: brown[900], borderRadius: 2}} label={paymentTypesObj[paymentType]} size="small" />
              </Stack>
              {status === 'PAID' ? (
                <Box>
                  <Button color="warning" onClick={() => setShowPaymentUpdateModal(true)}>Update Payment</Button>
                  <Button color="info" onClick={() => setShowPaymentHistoryModal(true)}>Details</Button>
                  <Button color="success" onClick={() => downloadPaymentReceipt(paymentId)}>Receipt</Button>
                  <ScrollDialog 
                    isFullScreen={false} 
                    maxWidth="xs"  
                    onModalClose={() => setShowPaymentHistoryModal(false)} 
                    open={showPaymentHistoryModal} 
                    title="Payment History"
                    // handleClose={onModalClose}
                    disableDialogActions={true}
                    showCloseIcon={true}
                  >
                    <InlineCircularProgress>
                      <PaymentHistory paymentId={paymentId}/>
                    </InlineCircularProgress>
                  </ScrollDialog>
                  <ScrollDialog 
                    isFullScreen={false} 
                    maxWidth="xs"  
                    onModalClose={() => setShowPaymentUpdateModal(false)} 
                    open={showPaymentUpdateModal} 
                    title="Payment Update"
                    // handleClose={onModalClose}
                    disableDialogActions={true}
                    showCloseIcon={true}
                  >
                    <PaymentUpdate paymentId={paymentId} onPaymentUpdated={() => {setShowPaymentUpdateModal(false); onPaymentSuccess()}} data={paymentUpdateData} />
                  </ScrollDialog>
                </Box>
              ): (
                isCollectPaymentsAllowed ? <>
                  <Button color="primary" onClick={() => setShowPaymentCollectionModal(true)}>Collect</Button>
                  <InmatePaymentModal 
                    onModalClose={onModalClose}
                    openModal={showPaymentCollectionModal}
                    // paymentData={data}
                    // inmateId={inmateId}
                    onPaymentSuccess={onPaymentSuccess}
                    paymentId={paymentId}
                  />
                </> : null
              )}
            </Stack>
          </CardActions>
        )}
      </Card>
    </Grid>
  );
}

function InmatePaymentModal({openModal, onModalClose, paymentId, inmateId, onPaymentSuccess}) {

  const isMinimumMediumDesktop = useMediaQuery(theme => theme.breakpoints.up('sm'));

  return(
    <ScrollDialog 
      isFullScreen={isMinimumMediumDesktop ? false: true} 
      maxWidth="xs"  
      onModalClose={onModalClose} 
      open={openModal} 
      title="Payment Collection"
      handleClose={onModalClose}
      disableDialogActions={true}
      showCloseIcon={isMinimumMediumDesktop ?  true: false}
    >
      <InlineCircularProgress>
        <InmatePaymentCollection paymentId={paymentId} inmateId={inmateId} closeModal={onModalClose} onPaymentSuccess={onPaymentSuccess}  />
      </InlineCircularProgress>
    </ScrollDialog>
  )
}
 
export default memo(PaymentCard);